import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import VideoCall from "@/components/VideoCall";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/context/WebSocketContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Video,
  VideoOff,
  FileText,
  Clock,
  User,
  Calendar,
  CheckCircle2,
  Loader2,
  Save,
  ArrowLeft,
  MessageSquare,
  ScrollText,
  AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SessionDetails = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { lastMessage } = useWebSocket();
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  
  // Video call state
  const [videoActive, setVideoActive] = useState(false);
  const [videoToken, setVideoToken] = useState(null);
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  
  // Real-time transcript state
  const [liveTranscript, setLiveTranscript] = useState([]);
  const liveTranscriptRef = useRef([]);
  
  // Notes state - separate from server data to prevent overwriting while typing
  const [notes, setNotes] = useState({
    pre_session_notes: "",
    during_session_notes: "",
    post_session_notes: "",
  });
  const [notesInitialized, setNotesInitialized] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Track if user is actively typing to prevent any state interference
  const isTypingRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  
  // Multi-role support
  const hasRole = (role) => user?.roles?.includes(role);
  const isTherapist = hasRole("therapist");
  const isFacilitator = hasRole("facilitator");
  const isAdmin = hasRole("admin");
  const canJoinVideo = isFacilitator || isTherapist;  // Both can join video calls
  const canManageSession = isTherapist || isAdmin;  // Can start/end calls, add notes

  // Use ref to track if we should keep polling
  const shouldPollRef = useRef(true);
  
  // Handle transcript updates from VideoCall component
  const handleTranscriptUpdate = (messages) => {
    setLiveTranscript(messages);
    liveTranscriptRef.current = messages;
  };
  
  // Listen for video_call_ended WebSocket event
  useEffect(() => {
    if (!lastMessage) return;
    
    if (lastMessage.type === "video_call_ended" && lastMessage.data?.session_id === sessionId) {
      // Force close the video call
      setVideoActive(false);
      setVideoToken(null);
      setCallEnded(true);
      shouldPollRef.current = false;
      toast.info("The therapist has ended the video call.");
      // Refresh session data
      fetchSession();
    }
  }, [lastMessage, sessionId]);
  
  useEffect(() => {
    fetchSession();
    // Poll for updates every 5 seconds
    const interval = setInterval(() => {
      if (shouldPollRef.current) {
        fetchSession();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [sessionId]);

  // Auto-join video when it becomes available for facilitators
  useEffect(() => {
    // Stop polling if video has ended or session is completed
    if (session?.video_call_ended || session?.outcome === 'completed' || callEnded) {
      shouldPollRef.current = false;
      // Ensure video is closed
      if (videoActive) {
        setVideoActive(false);
        setVideoToken(null);
      }
    }
    
    // Only auto-join if call hasn't ended
    if (session?.daily_room_url && !session?.video_call_ended && !callEnded && !videoActive && !videoToken && isFacilitator) {
      // GUARDRAIL: Only block auto-join for pure facilitators (not dual-role therapist/facilitator)
      if (session.facilitator_id && session.facilitator_id !== user?.id && !isTherapist) {
        console.warn(`[GUARDRAIL] Blocking auto-join: session.facilitator_id="${session.facilitator_id}" !== user.id="${user?.id}"`);
        // Don't auto-join - this isn't this facilitator's session
      } else {
        // Video is ready, auto-get token for facilitator (on behalf of patient)
        joinVideoCall();
      }
    }
    
    // If video was ended by therapist, close video on facilitator side
    if ((session?.video_call_ended || callEnded) && videoActive) {
      setVideoActive(false);
      setVideoToken(null);
      if (!callEnded) {
        setCallEnded(true);
        toast.info("The video call has ended.");
      }
    }
  }, [session?.daily_room_url, session?.video_call_ended, session?.outcome, videoActive, videoToken, isFacilitator, callEnded]);

  const fetchSession = async () => {
    // Skip fetch if user is actively typing to prevent note loss
    if (isTypingRef.current) {
      return;
    }
    
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const newSession = res.data;
      
      // Check if call has ended
      if (newSession.video_call_ended) {
        setCallEnded(true);
        if (videoActive) {
          setVideoActive(false);
          setVideoToken(null);
        }
      }
      
      // Video call ready notification removed - was causing repeated flashing
      // The UI already shows "Join Video Call" button when ready
      
      // Check if video was just ended
      if (session?.daily_room_url && !session?.video_call_ended && newSession.video_call_ended) {
        toast.info("The video call has ended.");
        setVideoActive(false);
        setVideoToken(null);
      }
      
      setSession(newSession);
      
      // Only initialize notes once on first load, not during polling
      // This prevents overwriting user's typed notes
      if (!notesInitialized && !isTypingRef.current) {
        setNotes({
          pre_session_notes: newSession.pre_session_notes || "",
          during_session_notes: newSession.during_session_notes || "",
          post_session_notes: newSession.post_session_notes || "",
        });
        setNotesInitialized(true);
      }
    } catch (error) {
      console.error("Error fetching session:", error);
      if (!session) {
        toast.error("Failed to load session details");
      }
    } finally {
      setLoading(false);
    }
  };

  const startVideoCall = async () => {
    setCreatingRoom(true);
    try {
      // Create room
      await axios.post(
        `${API}/video/create-room`,
        { session_id: sessionId, enable_transcription: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Get token
      const tokenRes = await axios.post(
        `${API}/video/token/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVideoToken(tokenRes.data);
      setVideoActive(true);
      
      // Refresh session data
      fetchSession();
      
      toast.success("Video call started");
    } catch (error) {
      console.error("Error starting video call:", error);
      toast.error(error.response?.data?.detail || "Failed to start video call");
    } finally {
      setCreatingRoom(false);
    }
  };

  const joinVideoCall = async () => {
    try {
      const tokenRes = await axios.post(
        `${API}/video/token/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVideoToken(tokenRes.data);
      setVideoActive(true);
      
      toast.success("Connected to video call", { duration: 2000 });
    } catch (error) {
      console.error("Error joining video call:", error);
      toast.error(error.response?.data?.detail || "Failed to join video call");
    }
  };

  const endVideoCall = async (transcriptFromCall) => {
    try {
      // Format the real-time transcript if we have it
      let realtimeTranscript = "";
      const transcriptMessages = transcriptFromCall || liveTranscriptRef.current;
      
      if (transcriptMessages && transcriptMessages.length > 0) {
        realtimeTranscript = transcriptMessages
          .map(msg => `[${new Date(msg.timestamp).toLocaleTimeString()}] ${msg.speaker}: ${msg.text}`)
          .join("\n");
        console.log(`Sending ${transcriptMessages.length} real-time transcript messages to server`);
        console.log("Transcript preview:", realtimeTranscript.substring(0, 500));
      } else {
        console.warn("No transcript messages to send. transcriptFromCall:", transcriptFromCall, "liveTranscriptRef:", liveTranscriptRef.current);
      }
      
      // End the video call and send real-time transcript
      console.log("API URL:", `${API}/video/end/${sessionId}`);
      console.log("Sending transcript to backend:", realtimeTranscript ? `${realtimeTranscript.length} chars` : "none");
      
      const response = await axios.post(
        `${API}/video/end/${sessionId}`,
        { realtime_transcript: realtimeTranscript },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      console.log("Video end response:", response.data);
      
      setVideoActive(false);
      setVideoToken(null);
      setCallEnded(true);
      setLiveTranscript([]);
      liveTranscriptRef.current = [];
      
      // Wait a moment for the database to update, then refresh session
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Fetch the updated session
      const updatedSession = await axios.get(
        `${API}/sessions/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log("Session after end call - transcript present:", !!updatedSession.data?.transcript);
      console.log("Session after end call - transcript length:", updatedSession.data?.transcript?.length || 0);
      
      // Update the session state with the new data including transcript
      setSession(updatedSession.data);
      
      // Show message based on whether transcript was captured
      const transcriptCount = transcriptMessages?.length || 0;
      if (response.data?.transcript_saved || updatedSession.data?.transcript) {
        toast.success(`Video call ended. Transcript saved! Add your notes and complete the session.`);
      } else {
        toast.success("Video call ended. Add your notes and complete the session when ready.");
      }
    } catch (error) {
      console.error("Error ending video call:", error);
      console.error("Error details:", error.response?.data);
      toast.error("Failed to end video call");
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/sessions/${sessionId}/notes`,
        notes,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHasUnsavedChanges(false);
      toast.success("Notes saved successfully");
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Failed to save notes");
    } finally {
      setSaving(false);
    }
  };

  const saveTranscript = async () => {
    try {
      const res = await axios.post(
        `${API}/sessions/${sessionId}/save-transcript`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      fetchSession();
    } catch (error) {
      console.error("Error saving transcript:", error);
      toast.error("Failed to save transcript");
    }
  };

  const completeSession = async () => {
    // First save the notes if there are unsaved changes
    if (hasUnsavedChanges) {
      setSaving(true);
      try {
        await axios.put(
          `${API}/sessions/${sessionId}/notes`,
          notes,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error("Error saving notes:", error);
        toast.error("Failed to save notes before completing");
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    setCompleting(true);
    try {
      await axios.put(
        `${API}/sessions/${sessionId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Session completed successfully!");
      // Navigate back to dashboard after short delay
      setTimeout(() => {
        navigate("/therapist");
      }, 1500);
    } catch (error) {
      console.error("Error completing session:", error);
      toast.error(error.response?.data?.detail || "Failed to complete session");
    } finally {
      setCompleting(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getStatusBadge = (outcome) => {
    const variants = {
      active: "bg-emerald-100 text-emerald-700 border-emerald-200",
      completed: "bg-blue-100 text-blue-700 border-blue-200",
      dropped: "bg-red-100 text-red-700 border-red-200",
      reassigned: "bg-amber-100 text-amber-700 border-amber-200",
    };
    return variants[outcome] || "bg-slate-100 text-slate-700";
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!session) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Session not found</p>
          <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="session-details">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-['Manrope']">Session Details</h1>
              <p className="text-muted-foreground">
                {isTherapist ? `Patient: ${session.patient_name}` : `Therapist: ${session.therapist_name}`}
              </p>
            </div>
          </div>
          <Badge className={getStatusBadge(session.outcome)}>
            {session.outcome.charAt(0).toUpperCase() + session.outcome.slice(1)}
          </Badge>
        </div>

        {/* GUARDRAIL: Show warning if facilitator (non-therapist) is on the wrong session */}
        {isFacilitator && !isTherapist && session.facilitator_id && session.facilitator_id !== user?.id && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border-2 border-red-400 rounded-lg" data-testid="wrong-session-warning">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-red-700">Warning: This is not your session</p>
              <p className="text-sm text-red-600 mt-1">
                This session was booked by a different facilitator. You should not join this video call.
                Please return to your dashboard and accept only calls for <strong>your own patients</strong>.
              </p>
              <Button variant="destructive" size="sm" className="mt-2" onClick={() => navigate("/facilitator")}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Return to My Dashboard
              </Button>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Call Section - Only show for active sessions, not completed ones */}
            {session.outcome === "active" && canJoinVideo && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Video className="w-5 h-5" />
                    Video Session
                  </CardTitle>
                  <CardDescription>
                    {callEnded || session.video_call_ended
                      ? "Video call has ended"
                      : session.daily_room_url 
                        ? "Video room is active" 
                        : isTherapist 
                          ? "Start a secure video call with the patient" 
                          : "Waiting for therapist to start the video call"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Call Ended State */}
                  {(callEnded || session.video_call_ended) ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-4">
                        <VideoOff className="w-8 h-8 text-slate-500" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-700 mb-2">Video Call Ended</h3>
                      <p className="text-muted-foreground mb-4">
                        The video session has been completed.
                      </p>
                      {isFacilitator && (
                        <Button variant="outline" onClick={() => navigate("/facilitator")}>
                          Return to Dashboard
                        </Button>
                      )}
                    </div>
                  ) : videoActive && videoToken ? (
                    <VideoCall
                      roomUrl={videoToken.room_url}
                      token={videoToken.token}
                      userName={isFacilitator ? `${user.name} (Facilitator for ${session.patient_name})` : user.name}
                      isOwner={videoToken.is_owner}
                      onTranscriptUpdate={handleTranscriptUpdate}
                      onLeave={(transcriptMessages) => {
                        setVideoActive(false);
                        setVideoToken(null);
                        // If we have transcript messages, save them
                        if (transcriptMessages && transcriptMessages.length > 0) {
                          liveTranscriptRef.current = transcriptMessages;
                        }
                        fetchSession();
                      }}
                      onCallEnd={isTherapist ? endVideoCall : (transcriptMessages) => {
                        // Facilitator received call ended signal from therapist
                        setVideoActive(false);
                        setVideoToken(null);
                        setCallEnded(true);
                        if (transcriptMessages && transcriptMessages.length > 0) {
                          liveTranscriptRef.current = transcriptMessages;
                        }
                        fetchSession();
                      }}
                    />
                  ) : (
                    <div className="text-center py-8">
                      {/* Video call ended - show completion state */}
                      {(session.video_call_ended || callEnded) ? (
                        <>
                          <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-8 h-8 text-blue-600" />
                          </div>
                          <h3 className="text-lg font-semibold text-blue-700 mb-2">Video Call Completed</h3>
                          <p className="text-muted-foreground mb-2">
                            Call ended at {formatDateTime(session.video_call_ended)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Review or add notes below, then complete the session.
                          </p>
                        </>
                      ) : session.daily_room_url ? (
                        <>
                          <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4 animate-pulse">
                            <Video className="w-8 h-8 text-emerald-600" />
                          </div>
                          <h3 className="text-lg font-semibold text-emerald-700 mb-2">Video Call is Ready!</h3>
                          <p className="text-muted-foreground mb-4">
                            {isFacilitator ? "The therapist is waiting. Join to connect the patient." : "A video call is in progress."}
                          </p>
                          <Button 
                            onClick={joinVideoCall} 
                            size="lg"
                            className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                          >
                            <Video className="w-5 h-5" />
                            {isFacilitator ? "Accept Call for Patient" : "Join Video Call Now"}
                          </Button>
                        </>
                      ) : isTherapist ? (
                        <>
                          <VideoOff className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                          <p className="text-muted-foreground mb-4">
                            No video call active. Start a call when ready.
                          </p>
                          <Button 
                            onClick={startVideoCall} 
                            disabled={creatingRoom}
                            className="gap-2"
                            data-testid="start-video-call"
                          >
                            {creatingRoom ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Video className="w-4 h-4" />
                            )}
                            Start Video Call
                          </Button>
                        </>
                      ) : (
                        <>
                          <Loader2 className="w-12 h-12 mx-auto text-muted-foreground mb-4 animate-spin" />
                          <p className="text-muted-foreground">
                            Waiting for therapist to start the video call...
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            This page will automatically update when the call is ready.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Video Call Ended Notice */}
            {session.video_call_ended && (
              <Card className="border-slate-300 bg-slate-50">
                <CardContent className="p-8 text-center">
                  <VideoOff className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-600 mb-2">Video Call Ended</h3>
                  <p className="text-muted-foreground">
                    The video session has been completed.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Session Completed Notice */}
            {session.outcome === "completed" && !session.video_call_ended && (
              <Card className="border-blue-300 bg-blue-50">
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-blue-500 mb-4" />
                  <h3 className="text-lg font-semibold text-blue-700 mb-2">Session Completed</h3>
                  <p className="text-muted-foreground">
                    This session has been marked as complete.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Notes Section - Only for Therapists */}
            {isTherapist && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Session Notes
                      </CardTitle>
                      <CardDescription>
                        Document your observations and treatment notes
                      </CardDescription>
                    </div>
                    <Button onClick={saveNotes} disabled={saving} className="gap-2">
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save Notes
                      {hasUnsavedChanges && !saving && (
                        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="pre" className="w-full">
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="pre" className="text-xs">Pre-Session</TabsTrigger>
                      <TabsTrigger value="during" className="text-xs">During</TabsTrigger>
                      <TabsTrigger value="post" className="text-xs">Post-Session</TabsTrigger>
                      <TabsTrigger value="transcript" className="text-xs">Transcript</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="pre" className="mt-4">
                      <div className="space-y-2">
                        <Label>Pre-Session Notes</Label>
                        <Textarea
                          placeholder="Document patient history, concerns, and session goals..."
                          className="min-h-[200px]"
                          value={notes.pre_session_notes}
                          onChange={(e) => {
                            setNotes({ ...notes, pre_session_notes: e.target.value });
                            setHasUnsavedChanges(true);
                            // Mark as typing to prevent fetch from overwriting
                            isTypingRef.current = true;
                            clearTimeout(typingTimeoutRef.current);
                            typingTimeoutRef.current = setTimeout(() => {
                              isTypingRef.current = false;
                            }, 2000);
                          }}
                          data-testid="pre-session-notes"
                        />
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="during" className="mt-4">
                      <div className="space-y-2">
                        <Label>During Session Notes</Label>
                        <Textarea
                          placeholder="Real-time observations and discussion points..."
                          className="min-h-[200px]"
                          value={notes.during_session_notes}
                          onChange={(e) => {
                            setNotes({ ...notes, during_session_notes: e.target.value });
                            setHasUnsavedChanges(true);
                            isTypingRef.current = true;
                            clearTimeout(typingTimeoutRef.current);
                            typingTimeoutRef.current = setTimeout(() => {
                              isTypingRef.current = false;
                            }, 2000);
                          }}
                          data-testid="during-session-notes"
                        />
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="post" className="mt-4">
                      <div className="space-y-2">
                        <Label>Post-Session Notes</Label>
                        <Textarea
                          placeholder="Summary, action items, and follow-up recommendations..."
                          className="min-h-[200px]"
                          value={notes.post_session_notes}
                          onChange={(e) => {
                            setNotes({ ...notes, post_session_notes: e.target.value });
                            setHasUnsavedChanges(true);
                            isTypingRef.current = true;
                            clearTimeout(typingTimeoutRef.current);
                            typingTimeoutRef.current = setTimeout(() => {
                              isTypingRef.current = false;
                            }, 2000);
                          }}
                          data-testid="post-session-notes"
                        />
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="transcript" className="mt-4">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <Label>Video Call Transcript</Label>
                          {session.daily_room_name && !session.video_call_ended && !callEnded && (
                            <Badge variant="outline" className="text-xs">
                              Transcription Active
                            </Badge>
                          )}
                          {!session.transcript && (session.video_call_ended || callEnded) && (
                            <Button variant="outline" size="sm" onClick={saveTranscript}>
                              <ScrollText className="w-4 h-4 mr-2" />
                              Retry Fetch
                            </Button>
                          )}
                        </div>
                        {session.transcript ? (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <CheckCircle2 className="w-3 h-3 text-green-500" />
                              Transcript captured ({session.transcript.length} characters)
                            </div>
                            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                              <pre className="whitespace-pre-wrap text-sm font-sans">{session.transcript}</pre>
                            </div>
                          </div>
                        ) : (session.video_call_ended || callEnded) ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>No transcript was captured for this call</p>
                            <p className="text-xs mt-1">
                              Transcripts require speech during the call. Click &quot;Retry Fetch&quot; to check again.
                            </p>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>Transcript will be available after the call ends</p>
                            <p className="text-xs mt-1">
                              Transcription is enabled and recording during the video call
                            </p>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            )}

            {/* AI Summary Section */}
            {session.ai_summary && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    AI Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-4">
                    <p className="text-sm">{session.ai_summary}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Session Info */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Session Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {isTherapist ? "Patient" : "Therapist"}
                    </p>
                    <p className="font-medium">
                      {isTherapist ? session.patient_name : session.therapist_name}
                    </p>
                  </div>
                </div>
                
                <Separator />
                
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Started</p>
                    <p className="font-medium">{formatDateTime(session.start_time)}</p>
                  </div>
                </div>
                
                {session.end_time && (
                  <>
                    <Separator />
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Ended</p>
                        <p className="font-medium">{formatDateTime(session.end_time)}</p>
                      </div>
                    </div>
                  </>
                )}
                
                {session.video_call_started && (
                  <>
                    <Separator />
                    <div className="flex items-center gap-3">
                      <Video className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Video Call</p>
                        <p className="font-medium text-sm">
                          Started: {formatDateTime(session.video_call_started)}
                        </p>
                        {session.video_call_ended && (
                          <p className="font-medium text-sm">
                            Ended: {formatDateTime(session.video_call_ended)}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            {isTherapist && session.outcome === "active" && (
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-2"
                    onClick={saveNotes}
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Save All Notes
                    {hasUnsavedChanges && (
                      <span className="ml-auto w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    )}
                  </Button>
                  {session.daily_room_name && !session.video_call_ended && !callEnded && (
                    <Button 
                      variant="outline" 
                      className="w-full justify-start gap-2"
                      onClick={saveTranscript}
                    >
                      <ScrollText className="w-4 h-4" />
                      Fetch Transcript
                    </Button>
                  )}
                  
                  {/* Complete Session Button - Always show for therapists during active session */}
                  <div className="pt-2 border-t">
                    {(session.video_call_ended || callEnded) ? (
                      <p className="text-xs text-muted-foreground mb-2">
                        Video call ended. Ready to complete session?
                      </p>
                    ) : session.video_call_started ? (
                      <p className="text-xs text-muted-foreground mb-2">
                        End the video call first, or complete session directly.
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground mb-2">
                        Complete this session when finished.
                      </p>
                    )}
                    <Button 
                      className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700"
                      onClick={completeSession}
                      disabled={completing || saving}
                      data-testid="complete-session-btn"
                    >
                      {completing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Completing...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          Complete Session
                        </>
                      )}
                    </Button>
                    {hasUnsavedChanges && (
                      <p className="text-xs text-amber-600 mt-1">
                        Notes will be saved automatically
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SessionDetails;
